import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Button, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import AvailabilityChip from '@/components/UI/AvailabilityChip/AvailabilityChip';
import DownloadCV from '@/components/UI/DownloadCV/DownloadCV';
import Reveal from '@/components/UI/Reveal/Reveal';
import Section from '@/components/UI/Section/Section';
import { reneKrajnc } from '@/constants/rene';
import { Routes } from '@/constants/routes';

import StatCard from '../StatCard/StatCard';
import classes from './HeroSection.module.scss';

const ctaButtonSx = {
  borderRadius: '999px',
  px: 3,
  py: 1.15,
  fontWeight: 700,
  textTransform: 'none',
};

const HeroSection = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const statCards = useMemo(
    () => [
      {
        title: t('hero.stats.experience.value'),
        description: t('hero.stats.experience.label'),
      },
      {
        title: t('hero.stats.seniority.value'),
        description: t('hero.stats.seniority.label'),
      },
      {
        title: t('hero.stats.specialization.value'),
        description: t('hero.stats.specialization.label'),
      },
    ],
    [t],
  );

  return (
    <Section
      alignment="center"
      background="default"
      shadow="large"
      gap={24}
      className={classes.heroSection}
    >
      <div className={classes.heroGrid}>
        {/* Not wrapped in <Reveal> — the image must be visible in the initial
            paint so Chrome records LCP the moment image bytes arrive.
            The slide-in is a pure CSS animation that never starts at opacity:0. */}
        <div className={classes.heroPortraitWrap}>
          <Image
            src="/images/rene-anime.png"
            alt={reneKrajnc}
            width={425}
            height={438}
            priority
            sizes="(max-width: 768px) 300px, 340px"
            className={classes.heroPortrait}
          />
        </div>

        <div className={classes.heroContent}>
          <Reveal>
            <AvailabilityChip />
          </Reveal>

          <Reveal delayMs={100}>
            <Typography variant="h2" component="p" className={classes.heroRole}>
              {t('role')}
            </Typography>
            <Typography variant="h1" component="h1" className={classes.heroName}>
              {reneKrajnc}
            </Typography>
          </Reveal>

          <Reveal delayMs={180}>
            <Typography variant="subtitle1" component="p" className={classes.heroTagline}>
              <Trans
                i18nKey="tagline"
                components={{
                  highlight: <span className={classes.highlight} />,
                }}
              />
            </Typography>
          </Reveal>

          <Reveal delayMs={260} className={classes.heroActions}>
            <Link href={Routes.CONTACT} locale={router.locale} className={classes.ctaLink}>
              <Button
                variant="contained"
                endIcon={<ArrowOutwardIcon />}
                className={classes.primaryCta}
                sx={ctaButtonSx}
              >
                {t('hero.cta.contact')}
              </Button>
            </Link>
            <Link href={Routes.PROJECTS} locale={router.locale} className={classes.ctaLink}>
              <Button
                variant="outlined"
                endIcon={<ArrowOutwardIcon />}
                className={classes.secondaryCta}
                sx={ctaButtonSx}
              >
                {t('hero.cta.projects')}
              </Button>
            </Link>
            <DownloadCV variant="outlined" className={classes.secondaryCta} />
          </Reveal>
        </div>
      </div>

      <Reveal delayMs={340} className={classes.heroStats}>
        {statCards.map((card, idx) => (
          <StatCard key={idx} {...card} />
        ))}
      </Reveal>
    </Section>
  );
};

export default HeroSection;
